import { User } from 'models/User.jsx';
import { CachedCall } from 'components/CachedCall.jsx';
import Fuse from 'fuse.js';
import { Request } from '../components/Request';
import Cookies from 'browser-cookies';
import { loggedInUserId } from 'session/loggedInUserId';

class UserCollection {

	constructor() {
		this.list = new Map();
		this.usernameList = new Map();
		this.lastSearchUpdate = CachedCall.inMinutes(1);
		this.cachedRemoteResultList = {};

		this.userAutocompleteSearchDebounced = _.debounce((func) => {
			func();
		}, 900, {});
	}

	async userAutocompleteSearch(username) {
		let defaultParameters = {
			autocomplete: true,
			json: true,
			search: username
		};
		return Request.apiv1Get('player/search', defaultParameters);
	}

	searchLocallyDelayed(username, limit = 50) {
		let localCancelled = false;
		const cancelLocal = () => {
			localCancelled = true;
		};
		const localResults = new Promise((resolve, reject) => {
			setTimeout(() => {
				if(localCancelled) {
					reject(new Error('Request was cancelled'));
				}
				const result = this.searchForUsers(username, limit);
				resolve(result);
			}, 400);
		});
		return {
			localResults,
			cancelLocal,
		};
	}

	_searchRemotely(username, limit = 50) {
		let remoteCancelled = false;
		let remoteResults = null;
		if(username.length < 3) {
			remoteResults = Promise.reject('Search too short for remote');
		}
		else {
			remoteResults = new Promise((resolve, reject) => {
				this.userAutocompleteSearchDebounced(() => {
					if(remoteCancelled) {
						reject(new Error('was cancelled'));
						return;
					}
					let resultsToUse = null;
					if(this.cachedRemoteResultList[username]) {
						resultsToUse = Promise.resolve(this.cachedRemoteResultList[username]);
					}
					else {
						resultsToUse = this.userAutocompleteSearch(username);
					}

					resultsToUse
						.then((result) => {
							this.cachedRemoteResultList[username] = result;
							if(remoteCancelled) {
								reject(new Error('was cancelled'));
								return;
							}
							const foundUsers = {};
							this.lastSearchUpdate.reset();
							_.each(result.users, (user) => {
								user = this.update(user);
								foundUsers[user.id] = user;
							});
							resolve(this.searchForUsers(username, limit));
						})
						.catch((error) => {
							reject(error);
							console.error(error);
						});
				});
			});
		}
		const cancelRemote = () => {
			remoteCancelled = true;
		};
		return { cancelRemote, remoteResults };
	}

	searchWithRequest(username, limit = 50) {
		const { localResults, cancelLocal } = this.searchLocallyDelayed(username, limit);
		const { remoteResults, cancelRemote } = this._searchRemotely(username, limit);

		let isCancelled = false;
		const cancel = () => {
			isCancelled = true;
			cancelLocal();
			cancelRemote();
		};
		const wasCancelled = () => {
			return isCancelled;
		};

		return {
			localResults: localResults,
			remoteResults,
			wasCancelled,
			cancelRemote,
			cancelLocal,
			cancel,
		};
	}

	searchForUsers(username, limit = 50) {
		if(!this.fuse || this.lastSearchUpdate.run()) {
			const fuseOptions = {
				shouldSort: true,
				threshold: 0.6,
				location: 0,
				distance: 200,
				maxPatternLength: 18,
				minMatchCharLength: 1,
				keys: [
					'username',
					'display_name',
				]
			};
			this.cachedLocalResultList = {};
			const userArray = Array.from(this.list.values()).map((user) => {
				return {
					id: user.id,
					username: user.usernameSearchString,
					display_name: user.displayNameSearchString,
				};
			});
			this.fuse = new Fuse(userArray, fuseOptions);
		}
		if(this.cachedLocalResultList[username]) {
			return this.cachedLocalResultList[username];
		}
		const results = this.fuse.search(username);
		return results.slice(0, limit).map((user) => {
			return this.findById(user.id);
		});
	}

	convertCollection(collection) {
		const newCollection = new Map();
		for(let i in collection) {
			if(collection.hasOwnProperty(i)) {
				const player = this.update(collection[i]);
				newCollection.set(player.id, player);
			}
		}
		return newCollection;
	}

	convertCollectionIntoPlainObjects(collection) {
		const newCollection = {};
		for(let i in collection) {
			if(collection.hasOwnProperty(i)) {
				const player = this.update(collection[i]);
				newCollection[player.id] = player;
			}
		}
		return newCollection;
	}

	create(user) {
		if(user && user.hasOwnProperty('id')) {
			const id = Number.parseInt(user.id);
			if(this.list.get(id)) {
				let previous = this.list.get(id);
				if(user.location && !previous.location) {
					previous.update({ location: user.location });
				}
				return previous;
			}
			user = User.create(user);
			this.list.set(id, user);
			return user;
		}
		else {
			return user;
		}
	}

	update(data) {
		const user = this.create(data);
		if(user) {
			user.update(data);
			return user;
		}
	}

	retrieveByUsername(username) {
		let usernameLowercase = null;
		if(username instanceof User) {
			usernameLowercase = username.usernameLowercase;
		}
		else {
			usernameLowercase = String(username).toLowerCase();
		}

		if(this.usernameList.get(usernameLowercase)) {
			return this.usernameList.get(usernameLowercase);
		}
		else if(usernameLowercase == myUser.usernameLowercase) {
			this.usernameList.set(myUser.usernameLowercase, myUser);
			return myUser;
		}
		return new User({ username: usernameLowercase });
	}

	retrieveById(userId) {
		let user = this.findById(userId);
		if(!user) {
			user = this.create({ id: userId });
		}
		return user;
	}

	findById(userId) {
		const found = this.list.get(Number.parseInt(userId));
		if(found) {
			return found;
		}
		return null;
	}

}

const Users = window.Users = new UserCollection();
if(myUser.id) {
	myUser.prefetchedData = true;
}
myUser.id = loggedInUserId({ serverSaysLoggedIn: window.isLoggedIn, cookieUserId: Cookies.get('lad_sock_user_id') });
myUser = Users.update(myUser);

User.serUsernameListHolder(Users.usernameList);
User.setUserIdListHolder(Users.list);
export { Users };

